/**
 * LiveView.tsx — tableau de bord studio en temps réel.
 * 100 % données réelles Supabase (aucune valeur mockée) :
 *  - KPIs = agrégats des tables projets / exports / templates (scoppés RLS).
 *  - Flux = derniers exports réels, mis à jour en direct via Supabase Realtime.
 *  - Activité = derniers projets réellement modifiés.
 * Le chrono affiche la durée réelle de la session de supervision.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FolderOpen,
  Printer,
  LayoutTemplate,
  HardDrive,
  Zap,
  ArrowUp,
  ChevronLeft,
  Clock,
  Inbox,
} from 'lucide-react';
import {
  studioStatsService,
  formatBytes,
  timeAgo,
  type StudioStats,
  type RecentExport,
} from '../services/StudioStatsService';

export const LiveView: React.FC = () => {
  const navigate = useNavigate();
  const [elapsed, setElapsed] = useState(0);
  const [stats, setStats] = useState<StudioStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [liveExports, setLiveExports] = useState<RecentExport[]>([]);
  const [liveCount, setLiveCount] = useState(0);

  // Chrono réel de la session de supervision.
  useEffect(() => {
    const t = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // Chargement initial des agrégats réels.
  const load = useCallback(async () => {
    const s = await studioStatsService.getStudioStats();
    setStats(s);
    setLiveExports(s.recentExports);
    setLiveCount(s.exportsCount);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Abonnement temps réel aux nouveaux exports (vraie donnée live).
  useEffect(() => {
    const unsubscribe = studioStatsService.subscribeToExports(exp => {
      setLiveExports(prev => [exp, ...prev].slice(0, 12));
      setLiveCount(c => c + 1);
    });
    return unsubscribe;
  }, []);

  const hours = Math.floor(elapsed / 3600);
  const mins = Math.floor((elapsed % 3600) / 60);
  const secs = elapsed % 60;
  const elapsedStr = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;

  const eventTitle = stats?.latestProjectName ?? 'Aucun projet';
  const eventSub = stats?.latestProjectUpdatedAt
    ? `Dernière activité ${timeAgo(stats.latestProjectUpdatedAt)}`
    : 'Studio Snapbooth';

  return (
    <div className="live-view" data-screen-label="Live">
      {/* Topbar */}
      <header className="live-topbar">
        <button
          className="sb-btn sb-btn-ghost sb-btn-sm"
          onClick={() => navigate('/dashboard')}
          aria-label="Retour au dashboard"
        >
          <ChevronLeft size={14} />
          Dashboard
        </button>

        <div className="live-topbar-brand">
          <div className="live-topbar-mark">
            <Zap size={18} />
          </div>
          <div>
            <div className="live-topbar-title">{eventTitle}</div>
            <div className="live-topbar-sub">{eventSub}</div>
          </div>
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="live-elapsed" aria-label="Durée de la session">
            {elapsedStr}
          </span>
          <span className="live-pulse-badge">
            <span className="live-pulse-dot" />
            En direct
          </span>
        </div>
      </header>

      <div className="live-content">
        {/* KPIs réels */}
        <div className="live-kpis">
          <Kpi
            label="Projets"
            value={loading ? '—' : (stats?.projectsCount ?? 0)}
            trend="vos designs"
            icon={FolderOpen}
          />
          <Kpi
            label="Templates"
            value={loading ? '—' : (stats?.templatesCount ?? 0)}
            trend="bibliothèque"
            icon={LayoutTemplate}
          />
          <Kpi
            label="Exports réalisés"
            value={loading ? '—' : liveCount}
            trend="en direct"
            icon={Printer}
          />
          <Kpi
            label="Volume exporté"
            value={loading ? '—' : formatBytes(stats?.totalExportBytes ?? 0)}
            trend="derniers exports"
            icon={HardDrive}
          />
        </div>

        {/* Flux exports + activité projets */}
        <div className="live-grid">
          <section className="live-panel" aria-label="Derniers exports">
            <div className="live-panel-title">
              <span>Derniers exports</span>
              <span style={{ fontSize: 11, color: 'var(--text-tertiary)', fontWeight: 500 }}>
                Temps réel
              </span>
            </div>
            {loading ? (
              <div className="live-empty">Chargement…</div>
            ) : liveExports.length === 0 ? (
              <div className="live-empty">
                <Inbox size={28} />
                <span>Aucun export pour l'instant. Exportez un design depuis l'éditeur.</span>
              </div>
            ) : (
              <div className="live-feed">
                {liveExports.map((exp, i) => (
                  <div
                    key={exp.id}
                    className="live-feed-photo"
                    style={{ animationDelay: `${i * 40}ms` }}
                    title={`${exp.filename} · ${timeAgo(exp.exportedAt)}`}
                  >
                    <span className="live-feed-format">{exp.format.toUpperCase()}</span>
                    <span className="live-feed-name">{exp.filename}</span>
                    <span className="live-feed-time">{timeAgo(exp.exportedAt)}</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="live-panel" aria-label="Activité récente">
            <div className="live-panel-title">
              <span>Activité récente</span>
              <span style={{ fontSize: 11, color: 'var(--text-tertiary)', fontWeight: 500 }}>
                Projets modifiés
              </span>
            </div>
            {loading ? (
              <div className="live-empty">Chargement…</div>
            ) : (stats?.recentProjects.length ?? 0) === 0 ? (
              <div className="live-empty">
                <Inbox size={28} />
                <span>Aucun projet récent.</span>
              </div>
            ) : (
              <div className="live-hardware-list">
                {(stats?.recentProjects ?? []).map(p => (
                  <button
                    key={p.id}
                    className="live-hw-row"
                    onClick={() => navigate(`/editor/${p.id}`)}
                    style={{ cursor: 'pointer', width: '100%', textAlign: 'left' }}
                  >
                    <FolderOpen size={14} />
                    <span className="live-hw-name">{p.name}</span>
                    <span className="live-hw-meta">
                      <Clock size={11} style={{ verticalAlign: '-1px', marginRight: 4 }} />
                      {timeAgo(p.updatedAt)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

interface KpiProps {
  label: string;
  value: number | string;
  trend: string;
  icon: React.ComponentType<{ size?: number }>;
}
const Kpi: React.FC<KpiProps> = ({ label, value, trend, icon: Icon }) => (
  <div className="live-kpi">
    <div className="live-kpi-head">
      <span className="live-kpi-label">{label}</span>
      <span className="live-kpi-icon">
        <Icon size={14} />
      </span>
    </div>
    <div className="live-kpi-value">
      {typeof value === 'number' ? value.toLocaleString('fr-FR') : value}
    </div>
    <span className="live-kpi-trend">
      <ArrowUp size={11} />
      {trend}
    </span>
  </div>
);
